import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useBeta } from './BetaContext';
import { getProjects } from '../api/projects';
import { getTicketsForUser } from '../api/tickets';
import { getPhasesByProject } from '../api/phases';
import { getUnreadCount } from '../api/notifications';
import { getBetaStatusOptions } from '../api/statusOptions';
import { getDepartmentsByCompany } from '../api/departments';
import { supabase } from '../supabaseClient';

const BetaDataContext = createContext({});

export const useBetaData = () => {
  const context = useContext(BetaDataContext);
  if (!context) {
    throw new Error('useBetaData must be used within BetaDataProvider');
  }
  return context;
};

/**
 * BetaDataProvider - Beta 模式的全域資料層
 *
 * 提供 projects, myTickets, notifications, unreadCount
 * 只在 isBetaMode=true 時載入資料
 *
 * myTickets 採 lazy-load 策略：初始載入不包含 myTickets（省 2 個 API），
 * 由消費者 view 呼叫 ensureMyTicketsLoaded() 按需載入。
 */
export const BetaDataProvider = ({ children }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { isBetaMode } = useBeta();

  const [projects, setProjects] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyMarquee, setCompanyMarquee] = useState(null);
  const [departmentMarquee, setDepartmentMarquee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectPhases, setProjectPhases] = useState({});
  const lastLoadKeyRef = useRef(null);
  const myTicketsLoadedRef = useRef(false);
  const myTicketsRef = useRef([]);

  // Load data when beta mode is active
  useEffect(() => {
    if (!isBetaMode || !profile?.id) {
      setLoading(false);
      return;
    }

    // 等待 AuthContext 完成 viewingCompanyId 初始化（初始值為 undefined）
    if (viewingCompanyId === undefined) return;

    // 防止相同參數重複載入資料（deps 變化但實質相同時）
    const loadKey = `${profile.id}_${viewingCompanyId}`;
    if (lastLoadKeyRef.current !== loadKey) {
      lastLoadKeyRef.current = loadKey;
      myTicketsLoadedRef.current = false;
      setProjectPhases({});
      loadAllBetaData();
    }

    // Realtime subscription — 每次 effect 重新執行都重新訂閱
    const channel = supabase
      .channel('beta-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          refreshUnreadCount();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [isBetaMode, profile?.id, viewingCompanyId]);

  // 載入公司用戶（直查 DB，departments 由外部傳入避免重複查詢）
  const loadCompanyUsers = async (deptList) => {
    const companyId = viewingCompanyId || profile?.company_id;
    if (!companyId) return [];

    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, email, name, avatar_url, role, company_id, department_id')
        .eq('approval_status', 'approved')
        .eq('is_active', true)
        .or(`company_id.eq.${companyId},role.eq.super_admin`)
        .order('name');

      if (error) {
        console.error('Error fetching company users:', error);
        return [];
      }

      const deptMap = {};
      (deptList || []).forEach(d => { deptMap[d.id] = d.name; });

      return (profiles || []).map(p => ({
        id: p.id,
        name: p.name || p.email?.split('@')[0] || p.email,
        email: p.email,
        avatar_url: p.avatar_url,
        role: p.role,
        company_id: p.company_id,
        department_id: p.department_id,
        department_name: deptMap[p.department_id] || '',
      }));
    } catch (err) {
      console.error('Error fetching company users:', err);
      return [];
    }
  };

  // 載入部門（直查 DB + cache）
  const loadDepartments = async () => {
    const companyId = viewingCompanyId || profile?.company_id;
    if (!companyId) return [];

    const { data } = await getDepartmentsByCompany(companyId);
    return data || [];
  };

  const loadAllBetaData = async () => {
    setLoading(true);

    // 先並行載入 departments 和其他不依賴 departments 的資料
    const [projectsRes, unreadRes, statusRes, deptsResult] = await Promise.all([
      getProjects({ companyId: viewingCompanyId }),
      getUnreadCount(profile.id),
      getBetaStatusOptions(viewingCompanyId, profile.department_id),
      loadDepartments(),
    ]);

    // departments 結果傳給 loadCompanyUsers 做 client-side join（省一次 departments 查詢）
    const usersResult = await loadCompanyUsers(deptsResult);

    setProjects(projectsRes.data || []);
    setUnreadCount(unreadRes.count || 0);
    setStatusOptions(statusRes.data || []);
    setCompanyUsers(usersResult || []);
    setDepartments(deptsResult);

    // 從 departments 結果提取跑馬燈（departments 已 join companies）
    if (deptsResult.length > 0) {
      setCompanyMarquee(deptsResult[0]?.company?.marquee_text ?? null);
      const myDept = deptsResult.find(d => d.id === profile.department_id);
      setDepartmentMarquee(myDept?.marquee_text ?? null);
    }

    setLoading(false);
  };

  // Lazy-load myTickets
  const ensureMyTicketsLoaded = useCallback(async () => {
    if (myTicketsLoadedRef.current) return myTicketsRef.current;
    if (!profile?.id) return [];
    myTicketsLoadedRef.current = true;
    const { data } = await getTicketsForUser(profile.id, { companyId: viewingCompanyId });
    const result = data || [];
    myTicketsRef.current = result;
    setMyTickets(result);
    return result;
  }, [profile?.id, viewingCompanyId]);

  const refreshProjects = useCallback(async () => {
    const { data } = await getProjects({ companyId: viewingCompanyId });
    setProjects(data || []);
  }, [viewingCompanyId]);

  const refreshMyTickets = useCallback(async () => {
    if (!profile?.id) return;
    myTicketsLoadedRef.current = true;
    const { data } = await getTicketsForUser(profile.id, { companyId: viewingCompanyId });
    const result = data || [];
    myTicketsRef.current = result;
    setMyTickets(result);
  }, [profile?.id, viewingCompanyId]);

  const refreshUnreadCount = useCallback(async () => {
    if (!profile?.id) return;
    const { count } = await getUnreadCount(profile.id);
    setUnreadCount(count || 0);
  }, [profile?.id]);

  const refreshStatusOptions = useCallback(async () => {
    if (!viewingCompanyId) return;
    const { data } = await getBetaStatusOptions(viewingCompanyId, profile?.department_id);
    setStatusOptions(data || []);
  }, [viewingCompanyId, profile?.department_id]);

  const refreshCompanyUsers = useCallback(async () => {
    const deptsResult = await loadDepartments();
    const result = await loadCompanyUsers(deptsResult);
    setCompanyUsers(result || []);
  }, [viewingCompanyId, profile?.company_id]);

  const refreshDepartments = useCallback(async () => {
    const result = await loadDepartments();
    setDepartments(result);
  }, [viewingCompanyId, profile?.company_id]);

  const usersMapByEmail = useMemo(() => {
    const map = {};
    for (const u of companyUsers) {
      if (u.email) map[u.email.toLowerCase()] = u;
    }
    return map;
  }, [companyUsers]);

  const refreshProjectPhases = useCallback(async (projectId) => {
    const { data } = await getPhasesByProject(projectId);
    const result = data || [];
    setProjectPhases((prev) => ({ ...prev, [projectId]: result }));
    return result;
  }, []);

  const updatePhaseDeliverableCache = useCallback((projectId, deliverableId, updates) => {
    setProjectPhases((prev) => {
      const phases = prev[projectId];
      if (!phases) return prev;
      return {
        ...prev,
        [projectId]: phases.map((p) => ({
          ...p,
          deliverables: (p.deliverables || []).map((d) =>
            d.id === deliverableId ? { ...d, ...updates } : d
          ),
        })),
      };
    });
  }, []);

  const updatePhaseItemCache = useCallback((projectId, itemId, updates) => {
    setProjectPhases((prev) => {
      const phases = prev[projectId];
      if (!phases) return prev;
      return {
        ...prev,
        [projectId]: phases.map((p) => ({
          ...p,
          deliverables: (p.deliverables || []).map((d) => ({
            ...d,
            deliverable_items: (d.deliverable_items || []).map((i) =>
              i.id === itemId ? { ...i, ...updates } : i
            ),
          })),
        })),
      };
    });
  }, []);

  const refreshAll = useCallback(() => {
    if (!isBetaMode || !profile?.id) return;
    lastLoadKeyRef.current = null;
    loadAllBetaData();
    if (myTicketsLoadedRef.current) {
      refreshMyTickets();
    }
  }, [isBetaMode, profile?.id, viewingCompanyId]);

  const value = {
    projects,
    myTickets,
    statusOptions,
    departments,
    unreadCount,
    companyUsers,
    companyMarquee,
    departmentMarquee,
    usersMapByEmail,
    projectPhases,
    loading,
    refreshProjects,
    refreshMyTickets,
    refreshStatusOptions,
    refreshDepartments,
    refreshUnreadCount,
    refreshCompanyUsers,
    refreshAll,
    ensureMyTicketsLoaded,
    refreshProjectPhases,
    updatePhaseDeliverableCache,
    updatePhaseItemCache,
  };

  return (
    <BetaDataContext.Provider value={value}>
      {children}
    </BetaDataContext.Provider>
  );
};
